import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import CreateDesignIllustration from '~/images/illustrations/create-show.svg';
import type {
  DesignList__Design as Design,
  DesignList__DesignFolder as Folder,
} from '../DesignList.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateDesignIllustration}
      header={t('pages.studio.list.empty_state.header')}
      description={t('pages.studio.list.empty_state.description')}
    />
  );
};

export interface StudioTableProps {
  designs: ReadonlyArray<Design>;
  folders: ReadonlyArray<Folder>;
  loading: boolean;
  search?: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

type DesignOrFolder = Design | Folder;

export const StudioTable = ({
  designs,
  folders,
  loading,
  onCheck,
  selectedIds,
  search = false,
}: StudioTableProps) => {
  const link = useLink();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  searchParams.set('page', '1');
  const combinedData = [...folders, ...designs];

  const columns = useColumns({ search });

  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isXLargeAndDown = useMediaQuery(theme.breakpoints.down('xl'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      createdAt: !isXLargeAndDown,
      designCount: !search && !isMediumAndDown,
      location: !isLargeAndDown,
      publishedApp: !isSmallAndDown,
      updatedAt: !isXLargeAndDown,
      status: !isSmallAndDown,
    }));
  }, [isLargeAndDown, isXLargeAndDown, isMediumAndDown, isSmallAndDown, search]);

  return (
    <ScrollableDataGrid
      checkboxSelection={!isSmallAndDown}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      isRowSelectable={({ row }) => row.__typename !== 'DesignFolder'}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowSelectionModelChange={onCheck}
      onRowClick={({ row }: GridRowParams<DesignOrFolder>) => {
        if (row.__typename === 'DesignFolder') {
          navigate({ ...link(`/studio/${row.id}`), search: searchParams.toString() });
          return;
        }
        navigate({
          ...link(`/studio/${row.designFolderId}/design/${row.id}`),
          search: searchParams.toString(),
        });
      }}
      getRowId={(row) => (row.__typename === 'Design' ? row.id : `DesignFolder-${row.id}`)}
      rows={combinedData}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
